import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle, ArrowLeft, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getPaymentEnvironment } from "@/lib/stripe";
import { trackConversion } from "@/lib/gtag";
import { trackRokuPurchase, trackRokuSubscribe, trackRokuStartTrial } from "@/lib/roku";
import { trackBillingFunnel } from "@/lib/billing-funnel";

export const Route = createFileRoute("/checkout/return")({
  component: CheckoutReturn,
  head: () => ({
    meta: [
      { title: "Payment Complete - Inquira" },
      { name: "description", content: "Your Inquira Pro subscription is now active." },
      { property: "og:title", content: "Inquira — Subscription confirmation" },
      { property: "og:description", content: "Check the status of your Inquira subscription." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function CheckoutReturn() {
  const [status, setStatus] = useState<"loading" | "success" | "failed">("loading");

  useEffect(() => {
    let cancelled = false;

    // Poll while the payment webhook activates the subscription.
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const { data: sub } = await supabase
          .from("subscriptions")
          .select("status, price_id")
          .eq("user_id", session.user.id)
          .eq("environment", getPaymentEnvironment())
          .in("status", ["active", "trialing"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (sub) {
          setStatus("success");
          trackBillingFunnel("purchase_complete");
          const value = sub.price_id === "inquira_pro_yearly" ? 99.99 : 9.99;
          trackConversion(value, "USD");
          // Roku: SUBSCRIBE for any confirmed new subscription; START_TRIAL
          // when it's a free trial; PURCHASE only for a confirmed paid one.
          // All deduped per session so refreshing this page never double-counts.
          trackRokuSubscribe(value, "USD", sub.status === "trialing" ? "trialing" : "active");
          if (sub.status === "trialing") trackRokuStartTrial();
          if (sub.status === "active") trackRokuPurchase(value, "USD");
          toast.success("Welcome to Inquira Pro! 🎉", {
            description: "Your verified Premium benefits, voice explanations, and practice mode are unlocked.",
          });
          return;
        }
      }

      if (attempt >= 9) {
        setStatus(session?.user ? "failed" : "success");
        return;
      }
      // Exponential backoff (1.5s → 8s) keeps the same 10-attempt ceiling with
      // far fewer requests while Stripe's webhook lands.
      setTimeout(() => poll(attempt + 1), Math.min(8000, 1500 * 1.5 ** attempt));
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "loading") {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="bg-card rounded-2xl p-8 border border-rule shadow-sm text-center space-y-4 max-w-md">
          <Loader2 className="size-8 text-neon-pink mx-auto animate-spin" />
          <p className="text-sm text-ink-muted">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="bg-card rounded-2xl p-8 border border-rule shadow-sm text-center space-y-4 max-w-md">
          <div className="size-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertCircle className="size-8 text-destructive" />
          </div>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
            Something went wrong
          </h1>
          <p className="text-sm text-ink-muted">
            We couldn't confirm your payment. If you were charged, your subscription will activate shortly.
          </p>
          <Link to="/">
            <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
              <ArrowLeft className="size-5" /> Back to Inquira
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
      <div className="bg-card rounded-2xl p-8 border border-rule shadow-sm text-center space-y-4 max-w-md">
        <div className="size-16 rounded-full bg-neon-yellow/20 flex items-center justify-center mx-auto">
          <CheckCircle className="size-8 text-neon-pink" />
        </div>
        <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
          Welcome to Pro! 🎉
        </h1>
        <p className="text-sm text-ink-muted">
          Your subscription is now active. Your confirmed study benefits and full Premium features are available.
        </p>
        <Link to="/">
          <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
            <ArrowLeft className="size-5" /> Start Solving
          </Button>
        </Link>
      </div>
    </div>
  );
}
